import { EntityBase } from '@provision/entity-types';
import { arrayIfy } from '@provision/formatting-utils';
import { useCallback } from 'react';
import { BulkUpdateSelectionInput } from 'src/graphql/entities/custom-view/custom-view-bulk-update';
import {
  BulkOption,
  getBulkOptionSubmitValue,
  getMutationErrorMessage,
} from 'src/pages/CustomViews/components/bulk-actions-modal.utils';

interface SelectedBulkAction {
  updateAttribute?: string;
  editorType: string;
  submitMode?: string;
}

interface SelectedAttrConfig {
  validation: {
    constraintKey?: string;
    accessRuleKey?: string[] | string;
  };
}

interface UseBulkActionSubmitParams {
  isSubmitInProgress: boolean;
  selectedBulkAction?: SelectedBulkAction;
  selectedAttrConfig?: SelectedAttrConfig;
  selectedBulkOptions: BulkOption[];
  editorValue: string;
  multiSelectValues: string[];
  snoozeUntilValue: string;
  requiresSnoozeUntilForValue: (value: string) => boolean;
  isLabelMutationAction: boolean;
  selectedCount: number;
  isServiceLevelBulkUpdate: boolean;
  selectedSiteBrandIds: string[];
  hasSubscriptionInSelectedSites: boolean;
  records: EntityBase[];
  associateClientsWithAddress: (params: {
    clientIds: string[];
    addressId: string;
    partnerId: number;
  }) => Promise<boolean>;
  normalizeEditorValue: (rawValue: string) => string | boolean;
  initBulkUpdate: (args: unknown) => Promise<{
    data?: { bulkUpdateEntities?: { jobId?: string } };
  }>;
  partnerId: number | null;
  entityName: string;
  selectionInput: BulkUpdateSelectionInput;
  showErrorAlert: (message: string) => void;
  showSuccessAlert: (message: string) => void;
  onBulkActionCompleted?: () => void;
  onBulkUpdateSubmitted: (jobId: string, operationKind?: 'update' | 'delete') => void;
  handleClose: () => void;
}

export function useBulkActionSubmit({
  isSubmitInProgress,
  selectedBulkAction,
  selectedAttrConfig,
  selectedBulkOptions,
  editorValue,
  multiSelectValues,
  snoozeUntilValue,
  requiresSnoozeUntilForValue,
  isLabelMutationAction,
  selectedCount,
  isServiceLevelBulkUpdate,
  selectedSiteBrandIds,
  hasSubscriptionInSelectedSites,
  records,
  associateClientsWithAddress,
  normalizeEditorValue,
  initBulkUpdate,
  partnerId,
  entityName,
  selectionInput,
  showErrorAlert,
  showSuccessAlert,
  onBulkActionCompleted,
  onBulkUpdateSubmitted,
  handleClose,
}: UseBulkActionSubmitParams) {
  const handleSubmitBulkUpdate = useCallback(
    async ({
      overrideEditorValue,
      overrideSnoozeUntilValue,
      overrideMultiSelectValues,
    }: {
      overrideEditorValue?: string;
      overrideSnoozeUntilValue?: string;
      overrideMultiSelectValues?: string[];
    } = {}) => {
      if (isSubmitInProgress) {
        return;
      }

      if (!selectedBulkAction?.updateAttribute) {
        return;
      }

      const effectiveEditorValue = overrideEditorValue ?? editorValue;
      const effectiveMultiSelectValues = overrideMultiSelectValues ?? multiSelectValues;
      const effectiveSnoozeUntilValue = overrideSnoozeUntilValue ?? snoozeUntilValue;
      const requiresSnoozeUntilValue = requiresSnoozeUntilForValue(effectiveEditorValue);
      const isMultiSelectEditor = selectedBulkAction.editorType === 'multi-select';
      const selectedOptionForSubmit = selectedBulkOptions.find(
        (option) =>
          getBulkOptionSubmitValue(option, selectedBulkAction.editorType) ===
          effectiveEditorValue,
      );
      const selectedOptionsForMultiSubmit = selectedBulkOptions.filter((option) =>
        effectiveMultiSelectValues.includes(
          getBulkOptionSubmitValue(option, selectedBulkAction.editorType),
        ),
      );

      const isValueMissing = isMultiSelectEditor
        ? effectiveMultiSelectValues.length === 0
        : !effectiveEditorValue;

      if (isValueMissing || (requiresSnoozeUntilValue && !effectiveSnoozeUntilValue)) {
        showErrorAlert('Required field is missing');
        return;
      }

      if (isLabelMutationAction && selectedCount > 200) {
        showErrorAlert('Label bulk update supports at most 200 selected rows');
        return;
      }

      if (isServiceLevelBulkUpdate && selectedSiteBrandIds.length !== 1) {
        showErrorAlert(
          'Bulk update Service Level requires selected sites to belong to a single brand',
        );
        return;
      }

      if (hasSubscriptionInSelectedSites) {
        showErrorAlert('Bulk update is not allowed for sites that are on a subscription');
        return;
      }
      const accessRuleKeys = arrayIfy(selectedAttrConfig?.validation?.accessRuleKey);

      const shouldValidateAgainstEnabledOptions = Boolean(
        selectedAttrConfig?.validation.constraintKey || accessRuleKeys.length > 0,
      );

      if (shouldValidateAgainstEnabledOptions) {
        const selectedOptionExists = isMultiSelectEditor
          ? selectedOptionsForMultiSubmit.length === effectiveMultiSelectValues.length
          : Boolean(selectedOptionForSubmit);

        if (!selectedOptionExists) {
          showErrorAlert(
            'Selected value is not available for the current partner configuration',
          );
          return;
        }
      }

      if (selectedBulkAction.submitMode === 'associate-clients-with-address') {
        const selectedPartnerIds = [
          ...new Set(
            records
              .map((record) =>
                Number((record as unknown as Record<string, unknown>).partnerId),
              )
              .filter((value) => Number.isFinite(value) && value > 0),
          ),
        ];

        if (selectedPartnerIds.length !== 1) {
          showErrorAlert(
            'Associating clients with a site requires selected clients to belong to a single partner',
          );
          return;
        }

        const clientIds = records
          .map((record) => {
            const clientId = (record as unknown as Record<string, unknown>).clientId;
            if (typeof clientId === 'string' || typeof clientId === 'number') {
              return String(clientId);
            }

            return typeof record.id === 'string' || typeof record.id === 'number'
              ? String(record.id)
              : '';
          })
          .filter(Boolean);

        const success = await associateClientsWithAddress({
          clientIds,
          addressId: String(normalizeEditorValue(effectiveEditorValue)),
          partnerId: selectedPartnerIds[0],
        });

        if (!success) {
          showErrorAlert('Failed to associate selected clients with site');
          return;
        }

        showSuccessAlert('Site was associated to selected clients. Undo is not available.');
        onBulkActionCompleted?.();
        handleClose();
        return;
      }

      try {
        const normalizedSubmittedValue = isMultiSelectEditor
          ? effectiveMultiSelectValues
          : normalizeEditorValue(effectiveEditorValue);
        const response = await initBulkUpdate({
          variables: {
            input: {
              partnerId: partnerId ?? 0,
              entityName,
              idempotencyKey: crypto.randomUUID(),
              selection: selectionInput,
              operation: {
                attribute: selectedBulkAction.updateAttribute,
                value: JSON.stringify(normalizedSubmittedValue),
                metadata: (() => {
                  const metadata: Record<string, unknown> = {};

                  if (requiresSnoozeUntilValue && effectiveSnoozeUntilValue) {
                    metadata.snoozeUntil = new Date(effectiveSnoozeUntilValue).toISOString();
                  }

                  if (
                    isServiceLevelBulkUpdate &&
                    typeof selectedOptionForSubmit?.platformSlaId === 'string' &&
                    selectedOptionForSubmit.platformSlaId
                  ) {
                    metadata.platformSlaId = selectedOptionForSubmit.platformSlaId;
                  }

                  return Object.keys(metadata).length ? JSON.stringify(metadata) : undefined;
                })(),
              },
            },
          },
        });

        const jobId = response.data?.bulkUpdateEntities?.jobId;
        if (!jobId) {
          showErrorAlert('Bulk update could not be started. Please check logs and try again.');
          return;
        }

        onBulkActionCompleted?.();
        onBulkUpdateSubmitted(jobId);
        handleClose();
      } catch (error) {
        showErrorAlert(getMutationErrorMessage(error));
      }
    },
    [
      associateClientsWithAddress,
      editorValue,
      entityName,
      handleClose,
      hasSubscriptionInSelectedSites,
      initBulkUpdate,
      isLabelMutationAction,
      isServiceLevelBulkUpdate,
      isSubmitInProgress,
      multiSelectValues,
      normalizeEditorValue,
      onBulkActionCompleted,
      onBulkUpdateSubmitted,
      partnerId,
      records,
      requiresSnoozeUntilForValue,
      selectedAttrConfig,
      selectedBulkAction,
      selectedBulkOptions,
      selectedCount,
      selectedSiteBrandIds.length,
      selectionInput,
      showErrorAlert,
      showSuccessAlert,
      snoozeUntilValue,
    ],
  );

  const handleSubmitBulkDelete = useCallback(
    async ({
      attributeKey,
      value,
      // Drives cache eviction of the selected records and the progress banner's wording, so it has
      // to say what the job actually did — not which submit path it took.
      operationKind = 'delete',
    }: {
      attributeKey: string;
      value?: string;
      operationKind?: 'update' | 'delete';
    }) => {
      if (isSubmitInProgress) {
        return;
      }

      try {
        const response = await initBulkUpdate({
          variables: {
            input: {
              partnerId: partnerId ?? 0,
              entityName,
              idempotencyKey: crypto.randomUUID(),
              selection: selectionInput,
              operation: {
                attribute: attributeKey,
                // Soft deletes carry no value; a host-context action (e.g. remove client from
                // sites) carries the value its page supplied.
                value: JSON.stringify(value ?? null),
              },
            },
          },
        });

        const jobId = response.data?.bulkUpdateEntities?.jobId;
        if (!jobId) {
          showErrorAlert('Bulk action could not be started. Please check logs and try again.');
          return;
        }

        onBulkActionCompleted?.();
        onBulkUpdateSubmitted(jobId, operationKind);
        handleClose();
      } catch (error) {
        showErrorAlert(getMutationErrorMessage(error));
      }
    },
    [
      entityName,
      handleClose,
      initBulkUpdate,
      isSubmitInProgress,
      onBulkActionCompleted,
      onBulkUpdateSubmitted,
      partnerId,
      selectionInput,
      showErrorAlert,
    ],
  );

  return { handleSubmitBulkUpdate, handleSubmitBulkDelete };
}
